<template>
  <CCard v-if="!loading && totalDisputes > 0">
    <CCardBody>
      <div class="mb-2">
        <div class="font-weight-medium">
          <CIcon name="cipAlertTriangle" size="custom-size" :height="18" class="mr-2"/>
          <translate translate-context="chargeback.recent">Recent Disputes</translate>
          <CBadge color="danger" class="ml-2">{{ totalDisputes }}</CBadge>
        </div>
      </div>

      <div class="user-items-list">
        <div
          v-for="dispute in disputes"
          :key="dispute.id"
          :class="['list-item', { 'list-item-current': isCurrentOrder(dispute) }]"
        >
          <div class="d-flex justify-content-between align-items-start">
            <div class="mr-2" style="min-width: 0">
              <template v-if="dispute.pica_services && dispute.pica_services.length">
                <div v-for="ps in dispute.pica_services" :key="ps.id">
                  <span class="font-weight-medium">{{ ps.name }}</span>
                </div>
              </template>
              <div>
                <span class="small text-muted">{{ dispute.order_number || `#${dispute.order_id}` }}</span>
                <CBadge
                  v-if="dispute.dispute_status"
                  :color="getStatusColor(dispute.dispute_status)"
                  class="badge-outline ml-2"
                >
                  {{ getStatusLabel(dispute.dispute_status) }}
                </CBadge>
                <span v-if="dispute.chargeback_type" class="small text-muted ml-2">
                  &middot; {{ dispute.chargeback_type.name }}
                </span>
              </div>
            </div>
            <div class="text-right text-nowrap">
              <strong class="text-danger">{{ dispute.chargeback_amount_eur|currency('EUR') }}</strong>
              <div class="small text-muted">{{ dispute.dispute_opened_at|formatDateTime('L LT') }}</div>
            </div>
          </div>
        </div>
      </div>
    </CCardBody>
  </CCard>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { DISPUTE_STATUS_LABELS, DISPUTE_STATUS_COLORS } from './consts'

export default {
  name: 'RecentChargebacks',
  props: {
    userId: {
      type: [Number, String],
      default: null
    },
    scope: {
      type: Object,
      default: () => ({ type: 'global', id: null })
    },
    currentOrderId: {
      type: [Number, String],
      default: null
    },
    excludeId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      disputes: [],
      totalDisputes: 0
    }
  },
  computed: {
    ...mapGetters('login', ['hasPerm']),
    canView() {
      const prefix = this.permissionPrefix
      if (!prefix) return false
      return this.hasPerm(`${prefix}.accounting.chargeback.list`)
    },
    permissionPrefix() {
      if (this.scope.type === 'pica-service') return 'pica_service'
      if (this.scope.type === 'contract-manager') return 'contract_manager'
      if (this.scope.type === 'global') return 'admin'
      return null
    }
  },
  mounted() {
    if (this.userId) {
      this.loadDisputes()
    }
  },
  watch: {
    userId(newVal) {
      if (newVal) {
        this.loadDisputes()
      }
    }
  },
  methods: {
    ...mapActions('accounting', ['fetchUserDisputes']),

    async loadDisputes() {
      if (!this.userId || !this.canView) return

      this.loading = true
      try {
        const result = await this.fetchUserDisputes({
          userId: this.userId,
          limit: 5,
          excludeId: this.excludeId
        })
        this.disputes = result.results || []
        this.totalDisputes = result.total || this.disputes.length
      } catch (error) {
        this.disputes = []
        this.totalDisputes = 0
      } finally {
        this.loading = false
      }
    },

    isCurrentOrder(dispute) {
      if (!this.currentOrderId) return false
      return String(dispute.order_id) === String(this.currentOrderId)
    },

    getStatusLabel(status) {
      return DISPUTE_STATUS_LABELS[status] || status
    },

    getStatusColor(status) {
      return DISPUTE_STATUS_COLORS[status] || 'secondary'
    }
  }
}
</script>

<style scoped>
.user-items-list {
  max-height: 500px;
  overflow-y: auto;
}

.list-item {
  display: block;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #ebedef;
  border-radius: 4px;
  color: inherit;
}

.list-item:last-child {
  border-bottom: none;
}

.list-item-current {
  background-color: #e8f4fd;
  border-left: 3px solid #321fdb;
}
</style>
